<template>
  <div class="AtlasMap" :style="{ height: `${height}px` }">
    <div class="AtlasMap-loader-container" v-if="isLoading">
      <atlas-loader />
    </div>
    <div
      class="AtlasMap-data-loading-container"
      v-if="!isLoading && isDataLoading"
    >
      <atlas-loader hideText />
    </div>

    <mgl-map
      class="AtlasMap-mapbox"
      ref="map"
      :access-token="accessToken"
      v-model:map-style="mapStyle"
      :data-is-loaded="isMapReady"
      :min-zoom="minZoom"
      @load="onMapLoad"
      @click="onMapClick"
      @mousemove="onMousemove"
      @mouseout="onMousemove"
      @zoomend="onMapZoom"
      @moveend="onMapMove"
      @sourcedata="onSourceData"
    >
      <mgl-navigation-control position="bottom-right" />

      <!-- vue-mapbox@0.4.1 -->
      <mgl-vector-layer
        ref="vectorLayer"
        type="fill"
        :key="sourceId + '_' + layerId"
        :replace="true"
        :source="sourceObj"
        :sourceId="sourceId"
        :layerId="layer.id"
        :layer="layer"
      />

      <!-- vue-mapbox@0.0.28 -->
      <!-- <mgl-vector-layer
      ref="vectorLayer"
      type="fill"
      :url="sourceObj.url"
      :tilesMinZoom="undefined"
      :tilesMaxZoom="undefined"
      :source="sourceObj"
      :sourceId="sourceId"
      :layerId="layer.id"
      :layer="layer"
    /> -->
    </mgl-map>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { debounce, isEqual } from 'lodash';
// import geoViewport from '@mapbox/geo-viewport';
// import getPolygonFromBbox from '@turf/bbox-polygon';
// import getCenter from '@turf/center';
import { mapActions, mapGetters, mapState } from 'vuex';
import { MglMap, MglNavigationControl, MglVectorLayer } from 'vue-mapbox';

export default defineComponent({
  name: 'atlas-map',

  emits: ['datum:mouseout', 'datum:mouseover', 'datum:toomuchdata'],

  components: {
    MglMap,
    MglNavigationControl,
    MglVectorLayer,
    // MglGeojsonLayer,
  },

  props: {
    breakdownId: {
      type: String,
      required: true,
    },

    height: {
      type: Number,
    },

    complexity: {
      type: String,
    },

    args: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    accessToken: '',
    mapStyle: '',
    mapUser: '',

    isLoading: true,
    isMapReady: false,
    dataLimited: false,
    lastGeoType: null,
    dataLoading: null,
    isRedrawing: null,
    isReprocessing: null,
    dataLastLoaded: null,
    fillColor: [
      'case',
      ['boolean', ['feature-state', 'hover'], false],
      'black',
      'rgba(0,0,0,0)',
    ],
    activeGeoTypeCoordinates: null,
    positionedFeatureId: null,
  }),

  watch: {
    height() {
      this.$nextTick(() => {
        if (this.$refs.map && this.$refs.map.map) this.$refs.map.map.resize();
      });
    },

    // TODO: review needs
    // Might be obsolete as geometry is not part of primaryGeo anymore.
    primaryGeo(val, prevVal) {
      if (!isEqual(val, prevVal) && val && val.geometry) {
        this.boundMap(['bbox', this.calculateBounds(val.geometry)], true);
      }
    },

    sliderValue() {
      this.reprocessData();
    },

    sliderContext() {
      this.reprocessData();
    },

    mapFilterData() {
      this.reprocessData();
    },

    geoType(val, prevVal) {
      if (val !== prevVal) {
        // If not the same, update lastGeoType.
        this.lastGeoType = prevVal;
      }

      // if (prevVal !== '08' && val === '08') {
      //   // If new geotype is 08, zoom.
      //   // eslint-disable-next-line no-unused-expressions
      //   this.$refs.map && this.$refs.map.map && this.boundMap(['position', {
      //     center: this.$refs.map.map.getCenter(),
      //     zoom: this.tractsZoom,
      //   }], true);
      // }

      if (!this.isMapReady) return;

      this.updateData();

      if (this.sliderValue > 0) {
        this.fetchMapFilterData().then(() => {
          this.reprocessData();
        });
      }
    },

    context() {
      if (!this.isMapReady) return;

      this.updateData();

      if (this.sliderValue > 0) {
        this.fetchMapFilterData().then(() => {
          this.reprocessData();
        });
      }
    },

    breakdown() {
      if (!this.isMapReady) return;

      this.updateData();
    },

    activeGeoType: {
      immediate: true,
      handler() {
        if (
          this.activeGeoType &&
          this.activeGeoType.position.length &&
          Number.isInteger(this.activeGeoType.zoom)
        ) {
          const position = [
            'position',
            {
              center: this.activeGeoType.position,
              zoom: this.activeGeoType.zoom,
            },
          ];
          this.activeGeoTypeCoordinates = position;
          if (this.isMapReady) this.boundMap(this.activeGeoTypeCoordinates);
        }
      },
    },
  },

  computed: {
    // Don't change the order of these state getters...
    ...mapState('breakdown', ['geoType', 'breakdownsData', 'primaryGeo']),

    ...mapState('breakdown/mapFilters', [
      'mapFilterData',
      'sliderValue',
      'mapFilterLoading',
    ]),

    ...mapGetters('meta', ['mapboxConfig']),

    ...mapGetters('breakdown', [
      'breakdownById',
      'contextByBreakdownId',
      'activeGeoType',
    ]),

    ...mapGetters('breakdown/mapFilters', ['sliderContext']),

    ...mapGetters('indicator', ['indicatorId', 'displayRev', 'geoCodes']),
    // ...

    breakdown() {
      return this.breakdownById(this.breakdownId);
    },
    breakdownData() {
      return this.breakdownsData[this.breakdownId] || [];
    },
    context() {
      return this.contextByBreakdownId(this.breakdownId);
    },

    isDataLoading() {
      return (
        this.dataLoading ||
        this.mapFilterLoading ||
        this.isRedrawing ||
        this.isReprocessing
      );
    },

    query() {
      return (this.$route || {}).query || {};
    },

    layer() {
      return {
        id: 'indicator',
        type: 'fill',
        source: this.sourceId,
        // This is the layer id within the tileset source. This represents the
        // filter combination.
        'source-layer': this.layerId,
        paint: this.paint,
      };
    },

    sourceId() {
      const { breakdown, geoType } = this;
      return breakdown &&
        breakdown.mapbox &&
        breakdown.mapbox.geoTypeSources &&
        breakdown.mapbox.geoTypeSources[geoType]
        ? breakdown.mapbox.geoTypeSources[geoType].sourceId ||
            breakdown.mapbox.geoTypeSources[geoType]
        : '';
    },

    layerId() {
      const { breakdown, geoType } = this;
      if (
        breakdown &&
        breakdown.mapbox &&
        breakdown.mapbox.geoTypeSources &&
        breakdown.mapbox.geoTypeSources[geoType] &&
        breakdown.mapbox.geoTypeSources[geoType].layerId
      ) {
        return breakdown.mapbox.geoTypeSources[geoType].layerId;
      }
      if (breakdown.mapbox.layerId) {
        return breakdown.mapbox.layerId;
      }
      return '';
    },

    sourceObj() {
      const { sourceId } = this;
      return {
        type: 'vector',
        url: `mapbox://${this.mapUser}.${sourceId}`,
      };
    },

    scheme() {
      return this.breakdown.scheme || ['#F8FAE9', '#BBD239', '#13B6EA'];
    },

    stops() {
      return this.scheme.map((hex, i) => [i, hex]);
    },

    paint() {
      if (this.mapboxObject && !this.dataLoading) {
        this.$nextTick(() => {
          this.mapboxObject.once('idle', this.onMapIdle);
        });
      }

      return {
        'fill-color': this.fillColor,
        'fill-opacity': [
          'case',
          // ...(this.args.show_null === undefined
          //   ? [['!', ['has', 'magnitude']], 0]
          //   : []),
          ['boolean', ['feature-state', 'hover'], false],
          1,
          0.7,
        ],
      };
    },

    tractsZoom() {
      return 9;
    },

    minZoom() {
      const { breakdown } = this;
      return breakdown.geoTypes &&
        breakdown.geoTypes.length === 1 &&
        breakdown.geoTypes[0] === '08'
        ? this.tractsZoom
        : 0;
    },

    initialPosition() {
      const { breakdown, calculateBounds, primaryGeo } = this;
      let type;
      let data;
      if (primaryGeo && primaryGeo.geometry) {
        // There is a primary geo, so return the bounds.
        type = 'bbox';
        data = calculateBounds(primaryGeo.geometry);

        // if (geoType === '08') {
        //   type = 'position';
        //   const polygon = getPolygonFromBbox(flatten(data));
        //   const centerPoint = getCenter(polygon);
        //   const center = centerPoint.geometry.coordinates;
        //   const zoom = tractsZoom;
        //   data = {
        //     center,
        //     zoom,
        //   };
        // }
      } else {
        type = 'position';

        // Defaults.
        const bayArea = [-122.42489316389134, 37.94837292294571];
        const center = breakdown.mapCenter || bayArea;
        const zoom = breakdown.mapZoom || 7;
        data = {
          center,
          zoom,
        };
      }

      return [type, data];
    },
  },

  methods: {
    ...mapActions('breakdown/mapFilters', ['fetchMapFilterData']),
    ...mapActions({
      fetchIndicatorGeoSelectorOptions:
        'indicator/fetchIndicatorGeoSelectorOptions',
    }),

    // mapStyle needs sync
    keepMapboxConfigInState() {
      this.accessToken = this.mapboxConfig.accessToken;
      this.mapStyle = this.mapboxConfig.style;
      this.mapUser = this.mapboxConfig.user;
    },

    calculateBounds(geo) {
      const bounds = geo?.geometry?.coordinates.reduce(
        (acc, cords) => {
          const preparedCoordinates = Array.isArray(cords[0][0])
            ? cords[0]
            : cords;

          preparedCoordinates.forEach(([lng, lat]) => {
            if (acc.min.lng === null || acc.min.lng > lng) {
              acc.min.lng = lng;
            }

            if (acc.max.lng === null || acc.max.lng < lng) {
              acc.max.lng = lng;
            }

            if (acc.min.lat === null || acc.min.lat > lat) {
              acc.min.lat = lat;
            }

            if (acc.max.lat === null || acc.max.lat < lat) {
              acc.max.lat = lat;
            }
          });
          return acc;
        },
        { min: { lat: null, lng: null }, max: { lat: null, lng: null } },
      );

      const boundsCoords = [
        [bounds.min.lng, bounds.min.lat],
        [bounds.max.lng, bounds.max.lat],
      ];
      return boundsCoords;
    },

    boundMap(position, animate = false) {
      const [type, data] = position;
      if (type === 'bbox') {
        const mapPadding = 100;
        const configs = {
          padding: {
            top: mapPadding,
            bottom: mapPadding,
            right: mapPadding,
            left: mapPadding,
          },
        };
        if (!animate) {
          configs.duration = 0;
        }
        this.$refs.map.map.fitBounds(data, configs);
      } else if (type === 'position') {
        // eslint-disable-next-line no-unused-expressions
        animate
          ? this.$refs.map.map.flyTo(data)
          : this.$refs.map.map.jumpTo(data);
      }
    },

    fixLayerOrder() {
      // Move the polygons behind the labels after the labels load in.
      const { layers } = this.$refs.map.map.getStyle();
      const firstSymbol = layers.find(({ type }) => type === 'symbol');

      if (this.$refs.vectorLayer) this.$refs.vectorLayer.move(firstSymbol.id);
    },

    async fetchIndicatorGeoOptions(option) {
      const isGeoCodeLoaded = this.geoCodes
        .flatMap((item) => Object.keys(item))
        .includes(option);

      if (!isGeoCodeLoaded) {
        return this.fetchIndicatorGeoSelectorOptions({
          id: this.indicatorId,
          displayRev: this.displayRev,
          geoCode: option,
        });
      }
      return null;
    },

    async onMapClick(event) {
      if (!event.mapboxEvent) return;

      const { point } = event.mapboxEvent || {};
      const [feature] = this.$refs.map.map.queryRenderedFeatures(point, {
        layers: ['indicator'],
      });

      if (feature && this.positionedFeatureId !== feature.id) {
        this.positionedFeatureId = feature.id;

        const calculatedBounds = this.calculateBounds(feature);

        this.boundMap(['bbox', calculatedBounds], true);

        await this.fetchIndicatorGeoOptions(feature.properties.geo_code_long);

        this.$router.push({
          ...this.$route,
          query: {
            ...this.$route.query,
            geo: feature.properties.geo_code_long,
          },
        });
      }
    },

    onMousemove(event) {
      // should probably refactor this :)
      if (!this.$refs.map || !this.isMapReady || !event.mapboxEvent) return;

      const { point } = event.mapboxEvent || {};

      const [feature] = this.$refs.map.map.queryRenderedFeatures(point, {
        layers: ['indicator'],
      });

      const geocode = feature?.properties?.geo_code_long;
      const mouseOver = this.mouseoverData[geocode];

      if (!feature || !feature.id || !point || !mouseOver) {
        if (this.hoverGeo) {
          this.$refs.map.map.setFeatureState(
            {
              source: this.sourceId,
              sourceLayer: this.layerId,
              id: this.hoverGeo,
            },
            {
              hover: false,
            },
          );
          this.hoverGeo = null;
        }
        this.$emit('datum:mouseout');
        return;
      }

      // No need to trigger anything if it's already hovered.
      if (this.hoverGeo === feature.id) return;

      if (this.hoverGeo) {
        this.$refs.map.map.setFeatureState(
          {
            source: this.sourceId,
            sourceLayer: this.layerId,
            id: this.hoverGeo,
          },
          {
            hover: false,
          },
        );
      }
      this.hoverGeo = feature.id;
      this.$refs.map.map.setFeatureState(
        {
          source: this.sourceId,
          sourceLayer: this.layerId,
          id: this.hoverGeo,
        },
        {
          hover: true,
        },
      );

      // Fetch data for mouseover
      this.$emit('datum:mouseover', mouseOver);
    },

    onMapMove() {
      if (this.dataLimited) {
        this.updateData(true);
      }
    },

    // When onMapZoom is called, onMapMove will also be called.
    onMapZoom() {
      const zoom = this.$refs.map.map.getZoom();

      if (this.geoType === '08' && zoom < this.tractsZoom) {
        if (this.hoverGeo) {
          this.$refs.map.map.setFeatureState(
            {
              source: this.sourceId,
              sourceLayer: this.layerId,
              id: this.hoverGeo,
            },
            {
              hover: false,
            },
          );
          this.hoverGeo = null;
        }
      }
    },

    onMapIdle() {
      this.isRedrawing = false;
    },

    onSourceData() {
      this.reprocessData();
      this.fixLayerOrder();
    },

    onMapLoad(event) {
      this.$nextTick(() => {
        this.fixLayerOrder();
        this.isMapReady = true;
        this.mapboxObject = event.map;
        if (!this.dataLoading) {
          this.mapboxObject.once('idle', this.onMapIdle);
        }

        this.$refs.map.map.resize();
        this.boundMap(
          this.activeGeoTypeCoordinates
            ? this.activeGeoTypeCoordinates
            : this.initialPosition,
        );
        this.updateData();
      });
    },

    waitForMapIdle() {
      return new Promise((resolve) => {
        this.mapboxObject.once('idle', resolve);
      });
    },

    waitForMapRender() {
      return new Promise((resolve) => {
        this.mapboxObject.once('render', () => {
          this.mapboxObject.once('idle', resolve);
        });
      });
    },

    updateData(force) {
      debounce(() => {
        this.updateDataExecute(force);
      }, 100)();
    },

    async updateDataExecute(force) {
      const changed =
        force ||
        !this.dataLastLoaded ||
        this.dataLastLoaded.indicatorId !== this.indicatorId ||
        this.dataLastLoaded.geoType !== this.geoType ||
        Object.keys(this.dataLastLoaded.context).some(
          (key) => this.dataLastLoaded.context[key] !== this.context[key],
        );
      if (!changed) {
        return;
      }

      this.dataLoading = true;
      this.dataLastLoaded = {
        action: 'get',
        indicatorId: this.indicatorId,
        geoType: this.geoType || '04',
        context: this.context,
        stops: this.stops,
        data: this.breakdownData,
      };

      await this.waitForMapIdle();

      // Query all rendered features from a single layer
      if (this.mapboxObject) {
        const features = this.mapboxObject.queryRenderedFeatures(null, {
          layers: ['indicator'],
          validate: false,
        });
        const allFeatures = this.mapboxObject.querySourceFeatures(
          this.sourceId,
          { sourceLayer: this.layerId },
        );
        let visibleGeos = features
          .map((f) => (f && f.properties ? f.properties.geo_code_long : null))
          .filter((gc) => gc);
        if (visibleGeos.length > 4000) {
          let limitedGeos = [];
          for (let i = 0; i < 40; i += 1) {
            const start = Math.floor(
              (visibleGeos.length - 100) * Math.random(),
            );
            limitedGeos = limitedGeos.concat(visibleGeos.splice(start, 100));
          }
          visibleGeos = limitedGeos;
          // visibleGeos = visibleGeos.slice(0, 5000);
          this.dataLimited = true;
          this.$emit('datum:toomuchdata', true);
        } else {
          this.$emit('datum:toomuchdata', false);
          this.dataLimited = allFeatures.length > features.length;
        }
        await this.$store.dispatch('breakdown/setGeos', { geos: visibleGeos });
        if (this.dataLimited) {
          this.dataLastLoaded.includeGeos =
            this.dataLimited && visibleGeos.length ? visibleGeos : null;
        }
      }

      // Determine the data column and mouseover column from `combinations`
      const combinations = this.breakdown.combinations || [];
      const activeCombo = combinations.find((comb) =>
        Object.keys(comb.contexts).every(
          (key) => comb.contexts[key] === this.context[key],
        ),
      );
      const dataColumn =
        activeCombo && activeCombo.fields ? activeCombo.fields.data_column : '';
      const mouseoverColumn =
        activeCombo && activeCombo.fields
          ? activeCombo.fields.mouseover_column_01
          : '';

      // Extract all different contexts with their defaults value and verify if there is a query_filter to be included as filter.
      const queryFilters = Object.keys(this.breakdown.queryFilters);
      const possibleFiltersFields = this.breakdown.contexts.reduce((acc, c) => {
        if (!queryFilters.includes(c.field)) return acc;
        return { ...acc, [`${c.field}`]: [c.defaultValue] };
      }, {});

      // Get query parameters.
      const { displayRev, ...queryParams } = this.$route.query;

      // Make the filters object based on the their default values and the query param filters
      const filteredQueryParams = Object.keys(queryParams).reduce((acc, p) => {
        if (Object.keys(possibleFiltersFields).includes(p))
          return {
            ...acc,
            [`${p}`]: [queryParams[p]],
          };
        return acc;
      }, possibleFiltersFields);
      const args = {
        geo_type: [this.geoType],
        ...filteredQueryParams,
        ...(this.context.year ? { year: [this.context.year] } : null),
        ...(this.dataLastLoaded.includeGeos
          ? { geo_code_long: this.dataLastLoaded.includeGeos }
          : null),
      };
      const fields = [dataColumn, mouseoverColumn];
      const res = await this.$api.fetchData({
        args,
        indicator_id: this.indicatorId,
        display_id: this.breakdownId,
        format: 'map',
        fields,
        displayRev: this.displayRev,
      });
      if (res && res.fillColor) {
        this.dataLastLoaded.data = res;
        this.reprocessData();
      }
    },

    reprocessData() {
      debounce(() => {
        this.isReprocessing = true;
        setTimeout(() => {
          this.reprocessDataExecute();
        }, 0);
      }, 100)();
    },

    reprocessDataExecute() {
      if (!this.dataLastLoaded || !this.dataLastLoaded.data.fillColor) return;
      const { total, fillColor } = this.dataLastLoaded.data;
      const fillColorLen = fillColor.length;
      const geos = (fillColorLen - 4) / 2;
      if (total && geos && total > geos) {
        this.dataLimited = true;
      }

      // Apply client-side filters to the Mapbox Fill Color data structure.
      let filteredFillColors = this.dataLastLoaded.data.fillColor;
      if (this.sliderValue > 0) {
        if (Object.keys(this.mapFilterData).length === 0)
          this.fetchMapFilterData();
        filteredFillColors = [];
        for (let i = 0; i < fillColorLen; i += 1) {
          const item = fillColor[i];
          // If this item is the start of a match on `geo_code_long`, then check
          // if the geo area's filter value meets the current criteria. If not,
          // skip it in the next render;
          if (
            Array.isArray(item) &&
            item[0] === '==' &&
            Array.isArray(item[1]) &&
            item[1][0] === 'get' &&
            item[1][1] === 'geo_code_long' &&
            this.mapFilterData &&
            Object.keys(this.mapFilterData).includes(item[2]) &&
            this.mapFilterData[item[2]] < this.sliderValue
          ) {
            i += 1;
          } else {
            filteredFillColors.push(item);
          }
        }
      }

      if (
        JSON.stringify(filteredFillColors) !== JSON.stringify(this.fillColor)
      ) {
        this.isRedrawing = true;
        setTimeout(() => {
          this.waitForMapRender().then(() => {
            this.isRedrawing = false;
          });
          this.fillColor = filteredFillColors;
        }, 0);
      }

      this.mouseoverData = this.dataLastLoaded.data.mouseoverData;
      this.isLoading = false;
      this.dataLoading = false;
      this.isReprocessing = false;
    },
  },

  async created() {
    this.keepMapboxConfigInState();
    if (!this.accessToken) throw new Error('Mapbox token not set.');

    const options = {};

    if (this.complexity) options.complexity = this.complexity;
  },
});
</script>

<style lang="scss">
.AtlasMap {
  position: relative;

  .AtlasMap-loader-container {
    position: absolute;
    z-index: 10000;
    width: 100%;
    height: 100%;
    background-color: rgba(248, 248, 248, 0.8);
  }

  &-mapbox {
    height: 100%;
  }

  .mapboxgl-ctrl-bottom-left,
  .mapboxgl-ctrl-bottom-right {
    margin-bottom: $tab-height;
  }
}

.AtlasMap-data-loading-container .AtlasLoader {
  position: absolute;
  width: 2%;
  top: 7%;
  left: 98%;
  overflow: hidden;
  max-width: 25px;
  min-width: 15px;
  z-index: 9999;
}

@import 'mapbox-gl/dist/mapbox-gl';
</style>
